import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';

import '../admin.css';
import instance from '../../../instance';
import {CircularProgress} from '@material-ui/core';
import CallNotification from '../../../context/NotificationContext';
import {PaymentMethod} from '../../../Enums/PaymentMethod';
import HelpPopover from '../../../ui/HelpPopover';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InvoicePopup from '../../PaymentPage/Invoice/InvoicePopup';
import {ConfirmDialog} from '../../../ui/ConfirmDialog';
import {
    IInvoice,
    IInvoiceDetail,
    InvoiceAction,
    IPaymentClientGroup,
    IPaymentSnapshot,
    IPaymentSnapshotItem,
} from '../../../interfaces/payment/IPaymentHistory';
import {IGetInvoiceListRequest, IGetPaymentInfoRequest, IInvoiceActionRequest} from '../../../interfaces/fetch/paymentHistory';

const invoiceActions: Record<InvoiceAction, {method: IInvoiceActionRequest['method']; status: number; statusName: string}> = {
    pay: {method: 'ConfirmPaymentByInvoice', status: 1, statusName: 'Оплачено'},
    cancel: {method: 'CancelInvoice', status: 3, statusName: 'Отменён'},
};

const InvoicesPage = () => {
    const [invoiceList, setInvoiceList] = useState<IInvoice[]>([]);
    const [visibleList, setVisibleList] = useState<IInvoice[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const [dateFrom, setDateFrom] = useState<string>('');
    const [dateTo, setDateTo] = useState<string>('');
    const [onlyPending, setOnlyPending] = useState<boolean>(true);

    const [selectedInvoice, setSelectedInvoice] = useState<IInvoiceDetail | null>(null);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const {handleErrorNotification} = CallNotification();

    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

    const [processingOrders, setProcessingOrders] = useState<Set<string>>(new Set());
    const [confirmAction, setConfirmAction] = useState<{action: InvoiceAction; invoice: IInvoice} | null>(null);
    const theme = useTheme();

    const openInvoice = (orderId: string, userId: number) => {
        setSelectedOrderId(orderId);
        setSelectedUserId(userId);
        setInvoiceOpen(true);
    };

    useEffect(() => {
        const fetchInvoiceList = async () => {
            try {
                const request: IGetInvoiceListRequest = {method: 'GetInvoiceList', params: {}};
                const response = await instance.post('/LK', request);
                handleErrorNotification(response);

                const sortedList = (response.data?.invoice_list || []).sort(
                    (a: IInvoice, b: IInvoice) => new Date(b.create_date).getTime() - new Date(a.create_date).getTime(),
                );

                setInvoiceList(sortedList);

                const filteredList = sortedList.filter((item: IInvoice) => item.payment_status === 0);

                setVisibleList(filteredList);
            } catch (error) {
                console.error('Ошибка при загрузке истории заказов', error);
            } finally {
                setLoading(false);
            }
        };

        void fetchInvoiceList();
    }, []);

    const handleRowClick = async (orderCode: string, userId: number) => {
        try {
            setSelectedUserId(null);

            const request: IGetPaymentInfoRequest = {
                method: 'GetPaymentInfo',
                params: {order: orderCode, user_id: userId},
            };
            const response = await instance.post('/LK', request);
            handleErrorNotification(response);

            if (response.data?.payment_info) {
                setSelectedInvoice(response.data.payment_info);
                setSelectedUserId(userId);
                setDetailOpen(true);
            }
        } catch (error) {
            console.error('Ошибка при получении информации о платеже', error);
        }
    };

    const handleClose = () => {
        setDetailOpen(false);
        setSelectedInvoice(null);
    };

    const applyFilter = () => {
        let filtered = invoiceList;

        // Фильтр по статусу оплаты
        if (onlyPending) {
            filtered = filtered.filter(item => item.payment_status === 0);
        }

        // Фильтр по дате
        if (dateFrom || dateTo) {
            const start = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
            const end = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

            let s = start,
                e = end;
            if (s && e && s.getTime() > e.getTime()) {
                const tmp = s;
                s = e;
                e = tmp;
            }

            filtered = filtered.filter(p => {
                const d = new Date(p.create_date).getTime();
                const okStart = s ? d >= s.getTime() : true;
                const okEnd = e ? d <= e.getTime() : true;
                return okStart && okEnd;
            });
        }

        setVisibleList(filtered);
    };

    // Обновляем фильтр при изменении галочки
    useEffect(() => {
        applyFilter();
    }, [onlyPending]);

    const runInvoiceAction = async (action: InvoiceAction, invoice: IInvoice) => {
        const {method, status, statusName} = invoiceActions[action];
        const orderCode = invoice.order_code;
        setConfirmAction(null);
        setProcessingOrders(prev => new Set(prev).add(orderCode));

        try {
            const request: IInvoiceActionRequest = {method, params: {order: orderCode}};
            const response = await instance.post('/LK', request);
            handleErrorNotification(response);

            if (response.data?.result === 'ok') {
                const update = (list: IInvoice[]) =>
                    list.map(item =>
                        item.order_code === orderCode ? {...item, payment_status: status, payment_status_name: statusName} : item,
                    );
                setInvoiceList(update);
                setVisibleList(update);
            }
        } catch (error) {
            console.error('Ошибка при обработке счёта', error);
        } finally {
            setProcessingOrders(prev => {
                const newSet = new Set(prev);
                newSet.delete(orderCode);
                return newSet;
            });
        }
    };

    const formatAmount = (amount: number) => `${new Intl.NumberFormat('ru-RU').format(amount)} ₸`;

    const renderInvoiceDetails = () => {
        if (!selectedInvoice) return null;

        let snapshot: IPaymentSnapshot | null;
        try {
            snapshot = JSON.parse(selectedInvoice.payment_snapshot);
        } catch {
            snapshot = null;
        }

        if (!snapshot || !Array.isArray(snapshot.items))
            return (
                <>
                    <Typography variant="subtitle1" gutterBottom>
                        Заказ № {selectedInvoice.order_code}
                    </Typography>
                    <Typography variant="body2">Дата: {new Date(selectedInvoice.create_date).toLocaleString()}</Typography>
                    <Typography variant="body2">Сумма: {selectedInvoice.total_amount} ₸</Typography>
                    <Typography variant="body2">Статус: {selectedInvoice.payment_status_name}</Typography>
                </>
            );

        const grouped = snapshot.items.reduce((acc: Record<number, IPaymentClientGroup>, item) => {
            if (!acc[item.facial_id]) {
                acc[item.facial_id] = {
                    facial_name: item.facial_name,
                    facial_inn: item.facial_inn,
                    items: [],
                };
            }
            acc[item.facial_id].items.push(item);
            return acc;
        }, {});

        const getPriceString = (item: IPaymentSnapshotItem) => {
            if (item.quantity && item.quantity > 1) {
                return `${item.quantity} x ${item.unit_price_no_vat} = ${item.price_no_vat}`;
            }
            return item.price_no_vat;
        };

        return (
            <>
                <Typography variant="subtitle1" gutterBottom>
                    Заказ № {selectedInvoice.order_code}
                </Typography>
                <Typography variant="body2">Дата: {new Date(selectedInvoice.create_date).toLocaleString()}</Typography>
                <Typography variant="body2">Сумма: {selectedInvoice.total_amount} ₸</Typography>
                <Typography variant="body2">Статус: {selectedInvoice.payment_status_name}</Typography>

                <Divider sx={{my: 2}} />

                {Object.values(grouped).map((group, idx) => (
                    <Box key={idx} sx={{mb: 3}}>
                        <Typography variant="subtitle2" sx={{mb: 1}}>
                            {group.facial_name} (ИНН: {group.facial_inn})
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Модель</TableCell>
                                    <TableCell>Серийный №</TableCell>
                                    <TableCell>Рег. №</TableCell>
                                    <TableCell>ОФД</TableCell>
                                    <TableCell>Без НДС</TableCell>
                                    <TableCell>С НДС</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group.items.map((item, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{item.model || '—'}</TableCell>
                                        <TableCell>{item.serial_number || '—'}</TableCell>
                                        <TableCell>{item.reg_number || '—'}</TableCell>
                                        <TableCell>{item.ofd?.name || '—'}</TableCell>
                                        <TableCell>{getPriceString(item)} ₸</TableCell>
                                        <TableCell>{item.price_with_vat} ₸</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                ))}

                <Divider sx={{my: 2}} />
                <Typography variant="body2">Итого без НДС: {snapshot.total_no_vat} ₸</Typography>
                <Typography variant="body2">
                    НДС{snapshot.vat_rate ? ` ${snapshot.vat_rate * 100}%` : ''}: {snapshot.vat} ₸
                </Typography>
                <Typography variant="body2">Итого с НДС: {snapshot.total_with_vat} ₸</Typography>
            </>
        );
    };

    return (
        <Box sx={{pt: 6, pb: 6}}>
            <Typography variant="h4" gutterBottom>
                Счета
            </Typography>

            <Paper sx={{mb: 2}}>
                <Box
                    component="form"
                    onSubmit={e => {
                        e.preventDefault();
                        applyFilter();
                    }}
                    p={2}
                    display="grid"
                    gridTemplateColumns={{xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)'}}
                    gap={2}
                    alignItems="end"
                >
                    <TextField
                        label="От"
                        type="date"
                        value={dateFrom}
                        onChange={e => setDateFrom(e.target.value)}
                        InputLabelProps={{shrink: true}}
                    />
                    <TextField
                        label="До"
                        type="date"
                        value={dateTo}
                        onChange={e => setDateTo(e.target.value)}
                        InputLabelProps={{shrink: true}}
                    />
                    <Box>
                        <Button variant="outlined" onClick={applyFilter} sx={{mr: 1, height: 40}}>
                            Найти
                        </Button>
                        <Button
                            variant="text"
                            onClick={() => {
                                setDateFrom('');
                                setDateTo('');
                                setOnlyPending(true);
                                const filtered = invoiceList.filter(item => item.payment_status === 0);
                                setVisibleList(filtered);
                            }}
                            sx={{height: 40}}
                        >
                            Сброс
                        </Button>
                    </Box>

                    <Box>
                        <FormControlLabel
                            control={<Checkbox checked={onlyPending} onChange={e => setOnlyPending(e.target.checked)} color="primary" />}
                            label="Только ожидающие оплаты"
                        />
                    </Box>
                </Box>
            </Paper>

            <Paper>
                <Box p={3}>
                    {loading ? (
                        <CircularProgress />
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата</TableCell>
                                    <TableCell>БИН/ИИН</TableCell>
                                    <TableCell>Наименование</TableCell>
                                    <TableCell>Номер заказа</TableCell>
                                    <TableCell>
                                        Счёт
                                        <HelpPopover content={<>Номер счёта при оплате через банковские реквизиты.</>} />
                                    </TableCell>
                                    <TableCell>Сумма</TableCell>
                                    <TableCell>Статус</TableCell>
                                    <TableCell>&nbsp;</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleList.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography color="text.secondary">Нет записей по выбранному фильтру</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    visibleList.map((item, index) => (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                height: '50px',
                                                cursor: 'pointer',
                                                '& td': {
                                                    borderBottom: index === visibleList.length - 1 ? 'none' : undefined,
                                                },
                                            }}
                                            onClick={() => void handleRowClick(item.order_code, item.user_id)}
                                        >
                                            <TableCell>{new Date(item.create_date).toLocaleString()}</TableCell>
                                            <TableCell>{item.payer_inn}</TableCell>
                                            <TableCell>{item.payer_name}</TableCell>
                                            <TableCell>{item.order_code}</TableCell>
                                            <TableCell sx={{paddingTop: 0, paddingBottom: 0}}>
                                                {item?.payment_method === PaymentMethod.Invoice ? (
                                                    <>
                                                        <Tooltip title="Скачать или распечатать">
                                                            <Button
                                                                size="small"
                                                                color="primary"
                                                                variant="text"
                                                                startIcon={<DescriptionOutlinedIcon sx={{fontSize: 16}} />}
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    openInvoice(item.order_code, item.user_id);
                                                                }}
                                                                sx={{fontSize: 12, height: 20}}
                                                            >
                                                                {item.invoice_number}
                                                            </Button>
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <>&mdash;</>
                                                )}
                                            </TableCell>
                                            <TableCell>{item.total_amount} ₸</TableCell>
                                            <TableCell sx={{color: item.payment_status === 1 ? 'success.main' : 'inherit', width: '80px'}}>
                                                {item.payment_status_name}
                                            </TableCell>
                                            <TableCell sx={{minWidth: '220px'}}>
                                                {item.payment_status === 0 && (
                                                    <Box display="flex" gap={1}>
                                                        <Tooltip title="Подтвердить оплату по счёту">
                                                            <Button
                                                                size="small"
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    setConfirmAction({action: 'pay', invoice: item});
                                                                }}
                                                                disabled={processingOrders.has(item.order_code)}
                                                                sx={{whiteSpace: 'nowrap', minWidth: '110px'}}
                                                            >
                                                                {processingOrders.has(item.order_code) ? 'Обработка...' : 'Оплатили'}
                                                            </Button>
                                                        </Tooltip>
                                                        <Tooltip title="Отменить неоплаченный счёт">
                                                            <Button
                                                                size="small"
                                                                variant="text"
                                                                color="error"
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    setConfirmAction({action: 'cancel', invoice: item});
                                                                }}
                                                                disabled={processingOrders.has(item.order_code)}
                                                                sx={{whiteSpace: 'nowrap'}}
                                                            >
                                                                Отменить
                                                            </Button>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Box>
            </Paper>

            <Dialog open={detailOpen} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>Детали платежа</DialogTitle>
                <DialogContent dividers>{selectedInvoice ? renderInvoiceDetails() : <Typography>Загрузка...</Typography>}</DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>

            <InvoicePopup
                open={invoiceOpen}
                setOpen={setInvoiceOpen}
                orderId={selectedOrderId}
                userId={selectedUserId}
                onClose={() => setInvoiceOpen(false)}
            />

            <ConfirmDialog
                open={confirmAction !== null}
                title={confirmAction?.action === 'cancel' ? 'Отмена счёта' : 'Оплата счёта'}
                content={
                    confirmAction && (
                        <>
                            <Typography sx={{mb: 1.5}}>
                                Счёт {confirmAction.invoice.invoice_number} от{' '}
                                {new Date(confirmAction.invoice.create_date).toLocaleDateString()}
                                {' на '}
                                {formatAmount(confirmAction.invoice.total_amount)}
                                {', '}
                                {confirmAction.invoice.payer_name} ({confirmAction.invoice.payer_inn}).
                            </Typography>
                            <Typography>
                                {confirmAction.action === 'cancel'
                                    ? 'Счёт будет отменён. Деньги по нему не проводились, ЦТО сможет сформировать новый счёт.'
                                    : 'Оплата будет зачислена на кошелёк ЦТО и распределена по кассам из заказа.'}
                            </Typography>
                        </>
                    )
                }
                confirmText={confirmAction?.action === 'cancel' ? 'Отменить счёт' : 'Подтвердить оплату'}
                cancelText="Закрыть"
                confirmColor={confirmAction?.action === 'cancel' ? theme.palette.error.main : undefined}
                onConfirm={() => {
                    if (confirmAction) void runInvoiceAction(confirmAction.action, confirmAction.invoice);
                }}
                onClose={() => setConfirmAction(null)}
            />
        </Box>
    );
};

export default InvoicesPage;
